import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Trash2, RotateCcw, Plus, Save } from 'lucide-react';
import { toast } from './notify';
import { money } from './VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const KINDS = [
  { kind: 'lab', field: 'lab_orders', title: 'Laboratory Tests', hasResult: true, stage: 'LAB_PENDING' },
  { kind: 'rad', field: 'rad_orders', title: 'Radiology / Imaging', hasResult: true, stage: 'RAD_PENDING' },
  { kind: 'dental', field: 'dental_orders', title: 'Dental Investigations', hasResult: true, stage: 'DENTAL_PENDING' },
  { kind: 'rx', field: 'prescriptions', title: 'Medications', hasResult: false },
  { kind: 'service', field: 'service_items', title: 'Services / Fees', hasResult: false },
];

const keyOf = (kind, item, idx) => (kind === 'rx' ? item.rx_id : kind === 'service' ? (item.service_id || String(idx)) : item.order_id);

// One editable section (table + redo + result edit + remove) for a single item kind on the superadmin visit editor.
export default function EditorItems({ visit, def, authCfg, reason, onChanged }) {
  const items = visit[def.field] || [];
  const [redo, setRedo] = useState([]);
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const vid = visit.visit_id;

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(okMsg + (res?.data?.voided_claim_id ? ` — claim ${res.data.voided_claim_id} voided, balance restored` : res?.data?.claim_resync ? ` — claim updated to $${res.data.claim_resync.new_total_usd}` : ''));
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = (item, idx) => call(
    () => axios.post(`${API}/admin/visits/${vid}/items/remove`, { kind: def.kind, key: keyOf(def.kind, item, idx), reason }, authCfg),
    `Removed ${item.item_name}`,
  );
  const saveResult = (item) => call(
    () => axios.put(`${API}/admin/visits/${vid}/items/result`, { kind: def.kind, order_id: item.order_id, result: results[item.order_id] ?? item.result ?? '', reason }, authCfg),
    `Result saved for ${item.item_name}`,
  );
  const redoSelected = () => call(
    () => axios.post(`${API}/admin/visits/${vid}/items/redo`, { kind: def.kind, order_ids: redo, reason }, authCfg),
    `${redo.length} test(s) sent back to ${def.title}`,
  ).then(() => setRedo([]));

  return (
    <div className="border border-gray-200 rounded-xl bg-white" data-testid={`editor-section-${def.kind}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-xl">
        <p className="text-sm font-bold text-gray-800">{def.title} <span className="text-gray-400 font-normal">({items.length})</span></p>
        {def.hasResult && redo.length > 0 && (
          <Button size="sm" onClick={redoSelected} disabled={busy} className="bg-violet-600 hover:bg-violet-700 h-7 text-xs" data-testid={`editor-redo-${def.kind}`}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Redo {redo.length} & return to {def.title}
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="p-3 text-sm text-gray-400">None on this visit.</p>
      ) : (
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className="text-xs text-gray-500">
              {def.hasResult && <th className="w-10 p-2" title="Select to redo" />}
              <th className="text-left p-2 font-semibold">Item</th>
              <th className="w-12 text-center p-2 font-semibold">Qty</th>
              <th className="w-24 text-right p-2 font-semibold">Total</th>
              <th className="w-28 text-left p-2 font-semibold">Status</th>
              {def.hasResult && <th className="text-left p-2 font-semibold w-2/5">Result</th>}
              {def.kind === 'rx' && <th className="text-left p-2 font-semibold w-1/4">Instructions</th>}
              <th className="w-24 p-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const removed = item.status === 'removed';
              const k = keyOf(def.kind, item, idx);
              return (
                <tr key={k} className={`border-t border-gray-100 ${removed ? 'opacity-50 line-through' : ''}`} data-testid={`editor-row-${k}`}>
                  {def.hasResult && (
                    <td className="p-2 text-center">
                      {item.status === 'completed' && (
                        <input type="checkbox" checked={redo.includes(item.order_id)} onChange={(e) => setRedo((p) => (e.target.checked ? [...p, item.order_id] : p.filter((x) => x !== item.order_id)))} data-testid={`editor-redo-check-${k}`} />
                      )}
                    </td>
                  )}
                  <td className="p-2 text-gray-800 break-words">{item.item_name}</td>
                  <td className="p-2 text-center">{item.quantity || 1}</td>
                  <td className="p-2 text-right">{money(visit, item.cost * (item.quantity || 1))}</td>
                  <td className="p-2 capitalize text-gray-600 text-xs">{(item.status || 'billable').replace(/_/g, ' ')}{item.previous_results?.length > 0 && <span className="block text-violet-600">redo ×{item.previous_results.length}</span>}</td>
                  {def.hasResult && (
                    <td className="p-2">
                      {removed ? <span className="text-gray-400">{item.result || '—'}</span> : (
                        <div className="flex gap-1">
                          <Input value={results[item.order_id] ?? item.result ?? ''} onChange={(e) => setResults((p) => ({ ...p, [item.order_id]: e.target.value }))} placeholder="No result yet" className="h-7 text-xs" data-testid={`editor-result-${k}`} />
                          <button onClick={() => saveResult(item)} disabled={busy} className="text-teal-600 hover:text-teal-800" title="Save result" data-testid={`editor-result-save-${k}`}><Save className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  )}
                  {def.kind === 'rx' && <td className="p-2 text-xs text-gray-600 break-words">{item.instructions || '—'}</td>}
                  <td className="p-2 text-right">
                    {!removed && (
                      <button onClick={() => remove(item, idx)} disabled={busy} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium" data-testid={`editor-remove-${k}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Pricelist search + kind picker to add an item to the visit.
export function AddItemBox({ visit, authCfg, priceList, reason, onChanged }) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('lab');
  const [qty, setQty] = useState(1);
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const matches = q.trim().length < 2 ? [] : priceList.filter((i) => i.item_name.toLowerCase().includes(q.toLowerCase()) || i.item_id.toLowerCase().includes(q.toLowerCase())).slice(0, 8);

  const add = async (item) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/visits/${visit.visit_id}/items/add`, { kind, item_id: item.item_id, quantity: Number(qty) || 1, instructions, reason }, authCfg);
      toast.success(`Added ${item.item_name} (${KINDS.find((k) => k.kind === kind).title})` + (res.data.claim_resync ? ` — claim updated to $${res.data.claim_resync.new_total_usd}` : ''));
      setQ('');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add item');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-teal-200 bg-teal-50/40 rounded-xl p-3" data-testid="editor-add-item">
      <p className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1"><Plus className="w-4 h-4 text-teal-600" /> Add item to visit</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {KINDS.map((k) => (
          <button key={k.kind} onClick={() => setKind(k.kind)} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${kind === k.kind ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'}`} data-testid={`editor-add-kind-${k.kind}`}>{k.title}</button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hospital price list (name or ID)…" className="flex-1 min-w-[200px] bg-white h-9" data-testid="editor-add-search" />
        <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="w-20 bg-white h-9" title="Quantity" data-testid="editor-add-qty" />
        {kind === 'rx' && <Input value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Dosage instructions" className="w-56 bg-white h-9" data-testid="editor-add-instructions" />}
      </div>
      {matches.length > 0 && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg divide-y max-h-64 overflow-y-auto">
          {matches.map((i) => (
            <button key={i.item_id} onClick={() => add(i)} disabled={busy} className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50 flex justify-between gap-2" data-testid={`editor-add-pick-${i.item_id}`}>
              <span className="truncate">{i.item_name} <span className="text-xs text-gray-400">{i.item_id} • {i.item_type}{i.uom ? ` • ${i.uom}` : ''}</span></span>
              <span className="font-medium text-gray-700 flex-shrink-0">{money(visit, i.cost)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
